/**
 * 문서 섹션 사이드바
 *
 * 보고서/문서형 화면에서 공통으로 쓰는 접기 가능한 목차 사이드바.
 * compare 도메인 여부와 무관하게 섹션 목록, active section, period chip만 다룬다.
 *
 * 다운로드(오프라인 HTML) 지원:
 * - logoSrc: 오프라인 파일은 경로 접근이 불가하므로 data URI 로 로고를 주입한다.
 * - hideUnchanged: 변경 없는 섹션/카테고리를 unmount 대신 hidden 으로 숨겨, 다운로드
 *   clone 이 전체 목차를 담고 inline JS 가 토글할 수 있게 한다.
 * - data-* 속성(ATTR): standalone-runtime 스크립트가 사이드바 토글/섹션 버튼을
 *   읽기 위한 계약. @/utils/dom-attrs 단일 출처를 공유한다.
 */

import {
  PeriodChip,
  TypeChip,
  YearChip,
  type ReportPeriod,
  type ReportType,
} from "@/components/common/report-chip";
import { cn } from "@/lib/utils";
import { ATTR } from "@/utils/dom-attrs";
import { Sidebar, SidebarContent, useSidebar } from "@/styles/ui/sidebar";
import { Badge } from "@/toggle-ui-kit/badge";
import { ScrollbarArea } from "@/toggle-ui-kit/scrollbar";
import IconButton from "@/toggle-ui-kit/icon-button";
import { InvectorLogo } from "@/toggle-ui-kit/logo";
import { PanelLeft, PanelLeftOpen } from "lucide-react";
import { memo, type ReactNode, useCallback, useRef } from "react";
import {
  type SidebarPeek,
  useSidebarPeek,
  useSidebarPeekOpen,
} from "./use-sidebar-peek";

export interface DocumentPeriodInfo {
  year: string;
  period: string;
  reportType: string;
}

export interface DocumentSidebarSection {
  id: string;
  label: string;
  hasChanges: boolean;
}

export interface DocumentSidebarCategory {
  label: string;
  sections: DocumentSidebarSection[];
}

export interface DocumentSidebarProps {
  periodInfo?: DocumentPeriodInfo;
  categories: DocumentSidebarCategory[];
  activeSection?: string | null;
  onSectionClick?: (section: DocumentSidebarSection) => void;
  /**
   * 변경 사항만 보기: 변경 없는 섹션/카테고리를 숨긴다. unmount 대신 hidden 클래스를
   * 써서 다운로드 clone 이 전체 목차를 담고 inline JS 가 토글할 수 있게 한다.
   */
  hideUnchanged?: boolean;
  className?: string;
  /** 로고 이미지 src 오버라이드. 다운로드 HTML 은 오프라인이라 data URI 를 주입한다. 미지정 시 기본 경로. */
  logoSrc?: string;
  /** period info 아래, nav 위에 렌더할 슬롯. 비교 화면의 chip 필터 등에 사용. */
  topContent?: ReactNode;
  /** 활성 목차가 nav 가시 영역을 벗어났을 때 따라가는 방식. */
  activeSectionScrollBehavior?: ScrollBehavior;
  /** 로딩 등으로 사이드바 펼치기/접기와 hover 미리보기를 막을 때 사용한다. */
  interactionDisabled?: boolean;
  /** 사이드바 자체의 로고 행과 접기 버튼. 공용 MainHeader 가 둘 다 맡는 화면은 false (기본 true). */
  showLogoAndToggle?: boolean;
  /** 접힘 방식. icon = 아이콘 레일 + hover 미리보기(기본), offcanvas = 완전 숨김(DSD 리뷰 fold) */
  collapsible?: "icon" | "offcanvas";
  /** flat = DSD 리뷰 목차 */
  variant?: "default" | "flat";
  /**
   * 바깥 트리거의 hover 미리보기 — offcanvas 는 접히면 패널이 화면 밖이라 상단 바 토글이 트리거다
   * (useDocumentSidebarToggle 의 peek). 없으면 패널 자체가 트리거(icon 레일)로 같은 훅을 안에서 쓴다.
   */
  peek?: SidebarPeek;
}

const DocumentSidebarToggle = ({
  disabled = false,
}: {
  disabled?: boolean;
}) => {
  const { state, toggleSidebar } = useSidebar();
  const isExpanded = state === "expanded";

  return (
    <span
      {...{ [ATTR.sidebarToggle]: true }}
      data-sidebar-document-toggle
      className={cn(
        "absolute left-2 top-2.5 z-20",
        "transform-gpu transition-transform duration-500 ease-in-out",
        "group-data-[state=expanded]:translate-x-[calc(var(--sidebar-width)-3rem)] group-data-[state=expanded]:translate-y-0.5",
      )}
    >
      <IconButton
        variant="transparent"
        icon={isExpanded ? <PanelLeft /> : <PanelLeftOpen />}
        className={cn(
          "transition-[width,height,border-radius,background-color,color] duration-300",
          !isExpanded && "size-8 rounded-lg bg-label-normal text-static-white",
        )}
        aria-label={isExpanded ? "사이드바 접기" : "사이드바 펼치기"}
        title={isExpanded ? "사이드바 접기" : "사이드바 펼치기"}
        disabled={disabled}
        onClick={toggleSidebar}
      />
    </span>
  );
};

interface SidebarSectionButtonProps {
  section: DocumentSidebarSection;
  isActive: boolean;
  hideUnchanged: boolean;
  activeButtonRef?: (node: HTMLButtonElement | null) => void;
  onSectionClick?: (section: DocumentSidebarSection) => void;
}

const SidebarSectionButton = memo(function SidebarSectionButton({
  section,
  isActive,
  hideUnchanged,
  activeButtonRef,
  onSectionClick,
}: SidebarSectionButtonProps) {
  return (
    <button
      ref={activeButtonRef}
      {...{ [ATTR.sectionId]: section.id }}
      onClick={() => onSectionClick?.(section)}
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-1 rounded px-2 py-1",
        "label-2-medium text-left text-label-normal",
        "transition-colors",
        isActive
          ? "bg-primary-base text-static-white"
          : "hover:bg-neutral-800/[.045] active:bg-neutral-900/[.09]",
        hideUnchanged && !section.hasChanges && "hidden",
      )}
    >
      <span className="flex-1 truncate">{section.label}</span>
      {section.hasChanges && <Badge className="bg-sky-500 ring-white" />}
    </button>
  );
});

const ExpandedSidebar = ({
  periodInfo,
  categories,
  activeSection,
  onSectionClick,
  hideUnchanged = false,
  logoSrc,
  topContent,
  activeSectionScrollBehavior = "auto",
  showLogoAndToggle = true,
  variant = "default",
}: DocumentSidebarProps) => {
  const prevActiveRef = useRef<string | null | undefined>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const activeButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (!node || activeSection === prevActiveRef.current) return;
      prevActiveRef.current = activeSection;
      const nav = navRef.current;
      if (!nav) return;

      // scrollIntoView는 사이드바 밖의 스크롤 조상까지 움직일 수 있다. nav 자체만
      // 필요한 만큼 이동해 본문 스크롤 애니메이션과 경쟁하지 않게 한다.
      const navRect = nav.getBoundingClientRect();
      const buttonRect = node.getBoundingClientRect();
      const overflowTop = buttonRect.top - navRect.top;
      const overflowBottom = buttonRect.bottom - navRect.bottom;
      if (overflowTop < 0) {
        nav.scrollTo({
          top: nav.scrollTop + overflowTop,
          behavior: activeSectionScrollBehavior,
        });
      } else if (overflowBottom > 0) {
        nav.scrollTo({
          top: nav.scrollTop + overflowBottom,
          behavior: activeSectionScrollBehavior,
        });
      }
    },
    [activeSection, activeSectionScrollBehavior],
  );

  const hasPeriodInfo =
    periodInfo?.year || periodInfo?.period || periodInfo?.reportType;
  const periodChips = hasPeriodInfo && (
    <div
      className={cn(
        "flex shrink-0 items-center gap-1 px-3",
        // flat: 위 12px, 아래 여백 없이 라벨의 pt-4 가 16px 간격을 만든다
        variant === "default" ? "h-[44px] border-b border-line-normal" : "pt-3",
      )}
    >
      {periodInfo?.reportType && (
        <TypeChip
          type={periodInfo.reportType.toUpperCase() as ReportType}
          size="large"
        />
      )}
      {periodInfo?.year && <YearChip year={periodInfo.year} size="large" />}
      {periodInfo?.period && (
        <PeriodChip
          period={periodInfo.period.toUpperCase() as ReportPeriod}
          size="large"
        />
      )}
    </div>
  );

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col",
        variant === "flat"
          ? "bg-background-base"
          : "bg-background-gray-normal pb-6",
      )}
    >
      {showLogoAndToggle && (
        <div
          data-sidebar-document-header
          className="flex h-[52px] shrink-0 items-center px-3"
        >
          <div
            className={cn(
              "opacity-0 transition-opacity duration-300 ease-in-out",
              "group-data-[state=expanded]:opacity-100",
            )}
          >
            <InvectorLogo src={logoSrc} className="h-4 w-[85px]" />
          </div>
        </div>
      )}

      {variant === "default" && periodChips}

      {topContent}

      {/*
        ScrollbarArea(Radix, type="hover" 기본): hover 할 때만 스크롤바가 뜨고 목차 위에
        겹쳐 그려져 항목 폭을 깎지 않는다. 스크롤 되는 건 nav 가 아니라 Viewport 라
        활성 항목 따라가기용 navRef 를 그쪽으로 넘긴다.
        overscroll-contain: 맨 위·아래에서 더 밀어도 부모(문서)로 스크롤이 넘어가지 않게
        — fixed 패널만 남고 나머지가 끌려가는 현상 방지.
        [&>div]:!block: Radix 가 끼워 넣는 display:table wrapper 되돌리기 (truncate 깨짐 방지).
      */}
      <ScrollbarArea
        className="min-h-0 flex-1"
        viewportRef={navRef}
        viewportClassName="overscroll-contain [&>div]:!block"
      >
        <nav>
          {variant === "flat" && periodChips}
          {categories.map((category, index) => {
            const categoryHasChanges = category.sections.some(
              (s) => s.hasChanges,
            );
            return (
              <div
                key={category.label}
                data-dl-sidebar-cat
                className={cn(
                  "flex flex-col",
                  variant === "default" && "border-b border-line-normal",
                  hideUnchanged && !categoryHasChanges && "hidden",
                )}
              >
                {variant === "flat" && index > 0 && (
                  // 위 목록(pb-2) + mt-2 = 16px, 아래 라벨 pt-4 = 16px
                  <div
                    data-sidebar-group-divider
                    className="mx-3 mt-2 border-t border-line-normal"
                  />
                )}
                <div
                  className={cn(
                    "flex items-center gap-1 caption-1-semibold",
                    variant === "default"
                      ? "border-b border-line-normal bg-background-gray-normal px-4 py-2 text-label-normal"
                      : "px-3 pb-2 pt-4 text-label-alternative",
                  )}
                >
                  {category.label}
                </div>

                <div className="flex flex-col gap-2 px-2 py-2">
                  {category.sections.map((section) => {
                    const isActive = activeSection === section.id;

                    return (
                      <SidebarSectionButton
                        key={section.id}
                        section={section}
                        isActive={isActive}
                        hideUnchanged={hideUnchanged}
                        activeButtonRef={isActive ? activeButtonRef : undefined}
                        onSectionClick={onSectionClick}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </ScrollbarArea>
    </div>
  );
};

export const DocumentSidebar = (props: DocumentSidebarProps) => {
  const { state } = useSidebar();
  const ownPeek = useSidebarPeek(state === "collapsed");
  const peek = props.peek ?? ownPeek;
  const peekOpen = useSidebarPeekOpen(peek);
  const disabled = props.interactionDisabled;
  return (
    <Sidebar
      collapsible={props.collapsible ?? "icon"}
      previewOpen={!disabled && peekOpen}
      // icon 레일은 여기가 트리거, offcanvas 는 띄운 패널 위에 커서가 있는 동안 열어 둔다
      onMouseEnter={disabled ? undefined : peek.enter}
      onMouseLeave={peek.leave}
      className={cn(
        "group z-50 overflow-hidden",
        // hover 로 띄운 패널은 본문 위를 지나간다 — 본문의 sticky 목차 제목도 z-50 이라
        // 같은 z 면 DOM 순서상 본문이 이겨 패널이 제목에 가린다. 고정 펼침은 본문과
        // 겹치지 않으므로(제자리를 차지한다) 띄운 동안만 올린다.
        "group-data-[hover-preview=open]:z-[60]",
        // 닫힐 때 z 가 즉시 내려가면 폭이 줄어드는 500ms 동안 본문 제목이 패널 위로 올라온다.
        // z-index 를 폭 전환이 끝난 뒤에 내린다 (열릴 때는 지연 없음).
        "![transition-property:left,right,width,z-index] ![transition-duration:500ms,500ms,500ms,0s] ![transition-delay:0s,0s,0s,500ms]",
        "group-data-[hover-preview=open]:![transition-delay:0s]",
        "group-data-[state=expanded]:animate-sidebar-raise",
        props.variant === "flat"
          ? "bg-background-base"
          : "bg-background-gray-normal",
        // shadcn 패널은 뷰포트 fixed 라 위에 상단 바가 있으면 덮는다. AppPageShell 이 내려보내는
        // 높이만큼 내리고 그만큼 줄인다. 변수가 없으면 0 — 기존 페이지는 그대로.
        "top-[var(--main-header-height,0px)] h-[calc(100svh-var(--main-header-height,0px))]",
        props.className,
      )}
      variant="sidebar"
    >
      <SidebarContent className="relative flex w-[--sidebar-width] min-w-[--sidebar-width] gap-0 p-0">
        <div
          className={cn(
            "flex-1 overflow-hidden",
            "transition-opacity duration-300 ease-in-out",
            "group-data-[state=collapsed]:opacity-0 group-data-[state=collapsed]:pointer-events-none",
            "group-data-[hover-preview=open]:!opacity-100 group-data-[hover-preview=open]:!pointer-events-auto",
          )}
        >
          <ExpandedSidebar {...props} />
        </div>
        {props.showLogoAndToggle !== false && (
          <DocumentSidebarToggle disabled={props.interactionDisabled} />
        )}
      </SidebarContent>
    </Sidebar>
  );
};
